import { autobind } from 'core-decorators'
import { History } from 'history'
import { inject, observer } from 'mobx-react'
import * as React from 'react'
import { RecommendationInstance } from '../../../../../../models/Recommendation'
import { RequirementInstance } from '../../../../../../models/Requirement'
import { Button, ButtonBar, NextButton } from '../../../../../../shared/components/Button'
import { ContentContainerBody, SplitContentContainer } from '../../../../../../shared/components/Content'
import { Headlines, InnerContainer } from '../../../../../../shared/components/Elements'
import { Headline, SubHeadline } from '../../../../../../shared/components/Headlines'
import { Loading } from '../../../../../../shared/components/Loading'
import { SideInformation } from '../../../../../../shared/components/SideInformation'
import { Jaw, JawTranslation } from '../../../../../../shared/Constants'
import { styled } from '../../../../../../shared/styled-components'
import { CalculatorStoreInstance } from '../../../../../../stores/CalculatorStore'
import { SegmentedProgressBar } from '../../../result/SegmentedProgressBar'
import { JawReplacementRecommendationView } from '../replacement/JawReplacementRecommendationView'
import { JawRecommendationsList } from './JawRecommendationsList'

interface IProps {
  store?: CalculatorStoreInstance
  history?: History
  jaw: Jaw
}

interface IState {
  loading: boolean
  error: boolean
  configuringReplacement: boolean
}

@inject('store')
@observer
export class JawRecommendationsView extends React.Component<IProps, IState> {
  state: IState = {
    loading: true,
    error: false,
    configuringReplacement: false
  }

  // -------------------------------------------------------------------------- LIFECYCLE

  async componentDidMount () {
    const { jaw, store } = this.props

    // Redirect back if no requirements are set
    const requirement = this._getJawRequirement()
    if (!requirement || !requirement.hasRequirements()) {
      return store!.redirectToJawRequirements(jaw)
    }

    await this._loadRecommendations()
  }

  // -------------------------------------------------------------------------- HELPERS

  private _getJawRequirement (): RequirementInstance {
    const { jaw, store } = this.props
    return store!.offer.getJawRequirement(jaw)
  }

  private _getJawRecommendations (): RecommendationInstance[] {
    const { jaw, store } = this.props
    return store!.offer.getJawRecommendations(jaw)
  }

  @autobind
  private async _loadRecommendations () {
    const { store } = this.props

    this.setState({ loading: true })
    try {
      await store!.fetchRecommendations()
    } catch (e) {
      console.error(e)
      this.setState({ error: true })
    }
    this.setState({ loading: false })
  }

  // -------------------------------------------------------------------------- EVENTS

  @autobind
  private async _onRequirementsChanged () {
    this.setState({ configuringReplacement: false })
    await this._loadRecommendations()
  }

  @autobind
  private _onConfiguringReplacementChanged (state: boolean) {
    this.setState({ configuringReplacement: state })
  }

  @autobind
  private async _onNextClick () {
    if (this.state.configuringReplacement) {
      await this._onRequirementsChanged()
    }
    this.props.store!.goForward()
  }

  // -------------------------------------------------------------------------- RENDERING

  private _renderContent () {
    if (this.state.error) return <Error>Bei der Berechnung der Empfehlung für Ihren Zahnersatz ist ein Fehler aufgetreten.</Error>

    const requirement = this._getJawRequirement()
    const recommendations = this._getJawRecommendations()
    if (requirement.noTeeth) {
      return <JawReplacementRecommendationView
        jaw={this.props.jaw}
        onRequirementsChanged={this._onRequirementsChanged}
        onConfiguringReplacementChanged={this._onConfiguringReplacementChanged} />
    } else {
      return <JawRecommendationsList recommendations={recommendations} />
    }
  }

  render () {
    const { jaw, store } = this.props
    const { loading, configuringReplacement } = this.state

    return (<InnerContainer padded>
      <Headlines>
        <Headline centered>
          {configuringReplacement ?
            'Ihre Auswahl' :
            `Unsere Empfehlung - ${JawTranslation[jaw]}`}
        </Headline>
        <SubHeadline centered>
          {configuringReplacement ?
            'Sie haben folgende Optionen Ihre Auswahl für einen herausnehmbaren Zahnersatz anzupassen:' :
            'Durch Ihre Auswahl empfehlen wir Ihnen folgenden Zahnersatz:'}</SubHeadline>
      </Headlines>

      <SplitContentContainer>
        <ContentContainerBody>
          {loading
            ? <Loading />
            : this._renderContent()}
        </ContentContainerBody>
        <SideInformation
          backButtonDisabled={loading}
          nextButtonDisabled={loading}
          onBackClick={() => !loading && store!.goBack()}
          onNextClick={this._onNextClick}
          infoText={jaw === 'upper'
            ? 'der implacheck®-Implantatkostenrechner Ihnen Ihre individuelle Kalkulation kostenfrei per Mail zur Verfügung stellt?'
            : 'Sie als Angstpatient die Behandlung auch in Dämmerschlaf oder Vollnarkose durchführen können?'
          }
          longInfoText={jaw === 'upper'
            ? 'der implacheck®-Implantatkostenrechner Ihnen Ihre individuelle Kalkulation kostenfrei per Mail zur Verfügung stellt? Damit Sie aus unserem Rechner die größtmöglichen Vorteile ziehen erhalten Sie die Kalkulation der Implantatkosten für Ihre Zahnsituation per E-Mail. Sie können sich am Ende des Rechners eintragen und erhalten dadurch zusätzlich Zugriff auf exklusive Tipps rund um das Thema Implantologie und Ihre Zahngesundheit. Sie können den Rechner so nutzen, um den Heil- und Kostenplan Ihres Behandlers mit den Konditionen einer implacheck®-Versorgung zu vergleichen.'
            : 'Sie als Angstpatient die Behandlung auch in Dämmerschlaf oder Vollnarkose durchführen können? Im Falle einer nachgewiesenen Angststörung werden die Kosten von Ihrer Krankenkasse übernommen. Eine solche können Sie sich bei einem Neurologen oder Psychologen bescheinigen lassen. Viele unserer Behandler bieten Ihnen die Optionen Dämmerschlaf- oder Vollnarkose direkt in ihren Praxen an und beraten Sie im ersten Gespräch gerne individuell.'
          }/>
      </SplitContentContainer>

      <ButtonBar>
        <Button onClick={() => !loading && store!.goBack()} disabled={loading}>Zurück</Button>
        <SegmentedProgressBar segmentCount={2} segment={1} />
        <NextButton primary onClick={this._onNextClick} disabled={loading}>Weiter</NextButton>
      </ButtonBar>
    </InnerContainer>)
  }
}

const Error = styled.div`
  font-weight: bold;
  text-align: center;
  color: ${props => props.theme.primaryColor};
  font-size: 14px;
`
